import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import { styled, useMediaQuery } from "@mui/system";
import { CheckCircle, Cancel } from "@mui/icons-material";
import InvitationStatusDialog from "../../dialogs/InvitationStatusDialog";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import backgroundImage from "./../../../src/assets/images/bgImage.png";
import lawgptLogo from "./../../../src/assets/images/lawgptLogo.png";
import {
  acceptInvitationApi,
  checkInvitationStatusApi,
  verifyInvitationTokenApi,
} from "../../services/api/caseManagementApi";
import LogoLoader from "../../components/UI/LogoLoader";

const StyledImage = styled("img")(({ theme }) => ({
  maxWidth: "150px",
  height: "auto",
  objectFit: "contain",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "120px",
  },
}));

const InvitationResponse = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [error, setError] = React.useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [tokenPayload, setTokenPayload] = useState({});
  const [inviteStatus, setInviteStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [isActionDisabled, setIsActionDisabled] = useState(false);

  const handleResponse = async (isAccepted) => {
    if (isAccepted) {
      setIsAccepting(true);
      setIsActionDisabled(true);
    } else {
      setIsDeclining(true);
      setIsActionDisabled(true);
    }
    setError(null);

    try {
      const response = await acceptInvitationApi(
        tokenPayload?.inviteeId,
        tokenPayload?.senderId,
        isAccepted
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error("Failed to process the invitation.");
      }

      // Display success toast
      toast.success(
        `Invitation ${isAccepted ? "accepted" : "declined"} successfully.`
      );
      // Navigate after a slight delay to allow the user to see the toast
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsAccepting(false);
      setIsDeclining(false);
    }
  };

  const handleCloseDialog = () => {
    setErrorOpen(false);
    navigate("/");
  };

  useEffect(() => {
    const verifyToken = async (token) => {
      try {
        const response = await verifyInvitationTokenApi(token);

        if (response.status === 200) {
          const data = response.data;
          setTokenPayload(data);
          console.log("Token is valid:");
        } else {
          const errorData = response.data;
          console.log(errorData.message);
          setErrorOpen(true);
        }
      } catch (error) {
        console.error("Token verification error:", error);
      }
    };
    verifyToken(token);
  }, [token]);

  useEffect(() => {
    const checkInvitationStatus = async (senderId, email) => {
      if (!tokenPayload?.senderId || !tokenPayload?.email) return;
      try {
        const response = await checkInvitationStatusApi(senderId, email);

        if (response.status === 200) {
          const data = response.data;
          setInviteStatus(data.status);
          if (data.status === "accepted" || data.status === "declined") {
            setErrorOpen(true);
          }
        } else {
          const errorData = await response.json();
          console.error("Error checking invitation status:", errorData.message);
          return null;
        }
      } catch (error) {
        console.error("API error checking invitation status:", error);
        return null;
      } finally {
        setLoading(false);
      }
    };
    checkInvitationStatus(tokenPayload?.senderId, tokenPayload?.email);
  }, [tokenPayload]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100vh",
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "30%",
            margin: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
        </Box>
      ) : (
        <Card
          sx={{ maxWidth: 500, mx: "auto", mt: 5, p: 3, borderRadius: "8px" }}
        >
          <CardContent>
            <Box
              sx={{
                mb: { xs: 1, md: 3 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <StyledImage src={lawgptLogo} alt="LawGPT Logo" />
            </Box>
            <Typography
              sx={{ fontSize: { xs: "16px", sm: "20px" } }}
              gutterBottom
            >
              Accept Invite
            </Typography>
            <Typography sx={{ fontSize: { xs: "13px", sm: "14px" } }} paragraph>
              You have been invited to join the Case Management System as a
              lawyer. Please choose to accept or decline the invitation.
            </Typography>
            {error && (
              <Typography color="error" paragraph>
                {error}
              </Typography>
            )}
            <Button
              sx={{ textTransform: "none", mr: 2 }}
              variant="contained"
              color="primary"
              onClick={() => handleResponse(true)}
              disabled={isActionDisabled}
              startIcon={!isAccepting && <CheckCircle />}
            >
              {isAccepting ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
                  <Box>Accepting...</Box>
                </Box>
              ) : (
                "Accept"
              )}
            </Button>
            <Button
              sx={{ textTransform: "none" }}
              variant="outlined"
              color="error"
              onClick={() => handleResponse(false)}
              disabled={isActionDisabled}
              startIcon={!isDeclining && <Cancel />}
            >
              {isDeclining ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
                  <Box>Declining...</Box>
                </Box>
              ) : (
                "Decline"
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      <ToastContainer />
      <InvitationStatusDialog
        open={errorOpen}
        onClose={handleCloseDialog}
        inviteStatus={inviteStatus}
      />
    </Box>
  );
};

export default InvitationResponse;
